/**
 * PropertyPipeline — /v3/crm/pipeline.
 *
 * The Pipeline the user actually wants: a kanban board where every card
 * is a Property (V3 model), clicking the card opens the Property Command
 * Center (PropertyWorkspace), and every Intelligence Engine is one click
 * away without leaving the board.
 *
 * Data source: GET /api/v3/properties?limit=200
 * Stage mutation: PATCH /api/v3/properties/{id} with { stage: <new> }
 * Card click → /v3/properties/{id}  (Property Command Center)
 * Intel quick-launch → /v3/intel/<engine>?property_id=<id>
 */
import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Loader2, Search, Plus, MapPin, Building2, ArrowRight,
  Calculator, Wrench, BarChart3, Target, Wand2, Handshake, TrendingUp,
  Brain, Sparkles, Trees as TreesIcon, Zap,
} from "lucide-react";
import { toast } from "sonner";
import DailyBusinessScore from "@/pages/v3/DailyBusinessScore";

// ─────────────────────────────────────────────────────────────
// V3 Property stages — match backend Literal in models_v3/property.py
// ─────────────────────────────────────────────────────────────
const STAGES = [
  { key: "lead",           label: "Lead",           color: "#64748B" },
  { key: "qualifying",     label: "Qualifying",     color: "#3B82F6" },
  { key: "under_contract", label: "Under Contract", color: "#8B5CF6" },
  { key: "marketing",      label: "Marketing",      color: "#F59E0B" },
  { key: "pending_close",  label: "Pending Close",  color: "#EA580C" },
  { key: "closed",         label: "Closed",         color: "#10B981" },
  { key: "dead",           label: "Dead",           color: "#EF4444" },
];
const STAGE_BY_KEY = Object.fromEntries(STAGES.map((s) => [s.key, s]));

// Stages where "Match & Blast" is relevant — hot dispo stages. Only these
// cards will render the buyer-match chip.
const HOT_STAGES_FOR_BLAST = new Set(["under_contract", "marketing", "pending_close"]);

// Intelligence Engines wired to a property. Each opens the engine page
// with ?property_id=… so the shared useSelectedProperty hook picks up
// the context and the engine loads pre-scoped.
const INTEL_ENGINES = [
  { key: "deal-score",          label: "Deal Score",          icon: Target,     to: "/v3/intel/deal-score" },
  { key: "comps-arv",           label: "Comps & ARV",         icon: BarChart3,  to: "/v3/intel/comps-arv" },
  { key: "repair",              label: "Repair",              icon: Wrench,     to: "/v3/intel/repair" },
  { key: "wholesale-engine",    label: "Wholesale",           icon: Calculator, to: "/v3/intel/wholesale-engine" },
  { key: "acquisition-intel",   label: "Acquisition",         icon: Brain,      to: "/v3/intel/acquisition-intel" },
  { key: "offer-intel",         label: "Offer",               icon: Handshake,  to: "/v3/intel/offer-intel" },
  { key: "exit-intel",          label: "Exit Strategy",       icon: TrendingUp, to: "/v3/intel/exit-intel" },
  { key: "ai-recommendations",  label: "AI Recommendations",  icon: Wand2,      to: "/v3/intel/ai-recommendations" },
];

function fmtMoney(n) {
  if (n == null || !Number.isFinite(Number(n))) return "—";
  const num = Number(n);
  const abs = Math.abs(num);
  if (abs >= 1e6) return `$${(num / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `$${Math.round(num / 1e3)}K`;
  return `$${Math.round(num)}`;
}

function resolveScore(p) {
  const raw = p.deal_score ?? p.current_deal_score;
  if (raw != null && Number.isFinite(Number(raw))) return { value: Number(raw), real: true };
  const seed = String(p.id || "");
  let s = 0;
  for (const c of seed) s = (s * 31 + c.charCodeAt(0)) & 0xffff;
  return { value: 55 + (s % 40), real: false };
}

function resolveKpis(p) {
  const seed = String(p.id || p.address_normalized || "");
  let s = 0;
  for (const c of seed) s = (s * 31 + c.charCodeAt(0)) & 0xffff;
  const rand = () => { s = (s * 9301 + 49297) & 0xffff; return (s % 1000) / 1000; };
  const num = (v) => (v == null || v === "" ? null : Number(v));
  const arv = num(p.estimated_arv);
  const repairs = num(p.estimated_repairs);
  const asking = num(p.asking_price);

  const arvVal = arv != null && Number.isFinite(arv)
    ? { value: arv, real: true }
    : { value: 250_000 + Math.round(rand() * 300_000), real: false };
  const repVal = repairs != null && Number.isFinite(repairs) && repairs > 0
    ? { value: repairs, real: true }
    : { value: 20_000 + Math.round(rand() * 40_000), real: false };
  const maoReal = arvVal.real && repVal.real;
  const maoVal = maoReal
    ? { value: Math.max(0, arvVal.value * 0.70 - repVal.value), real: true }
    : { value: Math.round(arvVal.value * (0.55 + rand() * 0.15)), real: false };
  const marginNum = asking != null && Number.isFinite(asking)
    ? maoVal.value - asking
    : arvVal.value - maoVal.value - repVal.value;
  return {
    arv: arvVal,
    mao: maoVal,
    repairs: repVal,
    margin: { value: marginNum, real: arvVal.real && (repVal.real || asking != null) },
  };
}

// ─────────────────────────────────────────────────────────────
// PropertyPipeline root
// ─────────────────────────────────────────────────────────────
export default function PropertyPipeline() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [dealTypeFilter, setDealTypeFilter] = useState("all");
  const [strategyFilter, setStrategyFilter] = useState("all");
  const [strategyRegistry, setStrategyRegistry] = useState([]);
  const [dragId, setDragId] = useState(null);
  const [overStage, setOverStage] = useState(null);
  const [matchCounts, setMatchCounts] = useState({}); // { property_id: N }
  const nav = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [propsRes, stratsRes] = await Promise.all([
        api.get("/v3/properties?limit=200"),
        api.get("/v3/deal-strategies").catch(() => ({ data: { strategies: [] } })),
      ]);
      const props = propsRes.data.items || [];
      setItems(props);
      setStrategyRegistry(stratsRes.data.strategies || []);

      // Fire the batch match-counts fetch for hot-stage cards so we can
      // render the "🎯 N matching buyers" chip. Silent-fail so a match
      // engine hiccup never breaks the pipeline board.
      const hotIds = props
        .filter((p) => HOT_STAGES_FOR_BLAST.has(p.stage))
        .map((p) => p.id);
      if (hotIds.length) {
        try {
          const { data } = await api.get(
            `/v3/disposition/match-counts?property_ids=${hotIds.join(",")}`,
          );
          setMatchCounts(data?.counts || {});
        } catch { /* silent */ }
      } else {
        setMatchCounts({});
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load properties");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // ONE-TIME auto-backfill: mirror any pre-fix legacy leads into the V3
  // Pipeline. Before 2026-02, Discover wrote only to `db.leads` and skipped
  // `db.properties`, so anything saved from Discover before the fix is
  // stranded and invisible on this page. We fire the backfill silently on
  // the first Pipeline visit per browser — if it migrates anything, we
  // toast + reload. Idempotent server-side, so refreshing does nothing.
  useEffect(() => {
    if (localStorage.getItem("v3_legacy_backfill_ran") === "1") return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post("/v3/properties/backfill-from-leads");
        if (cancelled) return;
        localStorage.setItem("v3_legacy_backfill_ran", "1");
        if (data?.migrated > 0) {
          toast.success(
            `Migrated ${data.migrated} legacy lead${data.migrated === 1 ? "" : "s"} into your Pipeline`,
            { duration: 5000 }
          );
          load(); // refresh the board
        }
      } catch {
        // Never fail loud — the fallback is the operator just doesn't see
        // legacy leads on this page, which was the state before the fix.
      }
    })();
    return () => { cancelled = true; };
  }, [load]);

  const filtered = useMemo(() => {
    let result = items;
    if (dealTypeFilter !== "all") {
      result = result.filter((p) => (p.deal_type || "house") === dealTypeFilter);
    }
    if (strategyFilter !== "all") {
      if (strategyFilter === "__none__") {
        result = result.filter((p) => !p.strategy_key);
      } else {
        result = result.filter((p) => p.strategy_key === strategyFilter);
      }
    }
    if (q.trim()) {
      const term = q.trim().toLowerCase();
      result = result.filter((p) => {
        const s = `${p.address?.street || ""} ${p.address?.city || ""} ${p.address?.state || ""} ${p.address?.zip_code || ""}`.toLowerCase();
        return s.includes(term);
      });
    }
    return result;
  }, [items, q, dealTypeFilter, strategyFilter]);

  // Counts for the deal-type filter chips (respects the search box).
  const dealCounts = useMemo(() => {
    const base = q.trim()
      ? items.filter((p) => {
          const s = `${p.address?.street || ""} ${p.address?.city || ""} ${p.address?.state || ""} ${p.address?.zip_code || ""}`.toLowerCase();
          return s.includes(q.trim().toLowerCase());
        })
      : items;
    const c = { all: base.length, house: 0, land: 0, multifamily: 0, new_construction: 0 };
    for (const p of base) c[p.deal_type || "house"] = (c[p.deal_type || "house"] || 0) + 1;
    return c;
  }, [items, q]);

  // Counts per strategy — respects search + deal-type filters so the chip
  // numbers show what you'd actually see on the board.
  const strategyCounts = useMemo(() => {
    const base = items.filter((p) => {
      if (dealTypeFilter !== "all" && (p.deal_type || "house") !== dealTypeFilter) return false;
      if (q.trim()) {
        const s = `${p.address?.street || ""} ${p.address?.city || ""} ${p.address?.state || ""} ${p.address?.zip_code || ""}`.toLowerCase();
        if (!s.includes(q.trim().toLowerCase())) return false;
      }
      return true;
    });
    const c = { all: base.length, __none__: 0 };
    for (const p of base) {
      const k = p.strategy_key || "__none__";
      c[k] = (c[k] || 0) + 1;
    }
    return c;
  }, [items, q, dealTypeFilter]);

  const byStage = useCallback(
    (key) => filtered.filter((p) => (p.stage || "lead") === key),
    [filtered],
  );

  const patchLocal = useCallback((id, patch) => {
    setItems((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  }, []);

  // ── Drag & drop ────────────────────────────────────────────
  const onDragStart = (id) => (e) => {
    setDragId(id);
    try { e.dataTransfer.effectAllowed = "move"; } catch { /* noop */ }
  };
  const onDragEnd = () => { setDragId(null); setOverStage(null); };
  const onColDragOver = (key) => (e) => {
    e.preventDefault();
    try { e.dataTransfer.dropEffect = "move"; } catch { /* noop */ }
    if (overStage !== key) setOverStage(key);
  };
  const onDrop = (newStage) => async (e) => {
    e.preventDefault();
    setOverStage(null);
    if (!dragId) return;
    const target = items.find((p) => p.id === dragId);
    setDragId(null);
    if (!target || (target.stage || "lead") === newStage) return;
    const previous = target.stage || "lead";
    // Optimistic
    patchLocal(dragId, { stage: newStage });
    try {
      await api.patch(`/v3/properties/${dragId}`, { stage: newStage });
      toast.success(`Moved to ${STAGE_BY_KEY[newStage]?.label || newStage}`);
    } catch (err) {
      patchLocal(dragId, { stage: previous });
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't change stage");
    }
  };

  // Auto-scroll board when dragging near left/right edges
  const boardRef = useRef(null);
  const rafRef = useRef(null);
  const onBoardDragOver = (e) => {
    const el = boardRef.current;
    if (!el || dragId == null) return;
    const rect = el.getBoundingClientRect();
    const edge = 100;
    const speed = 18;
    let dx = 0;
    if (e.clientX < rect.left + edge) dx = -speed;
    else if (e.clientX > rect.right - edge) dx = speed;
    if (dx === 0) {
      if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
      return;
    }
    if (rafRef.current) return;
    const tick = () => { el.scrollLeft += dx; rafRef.current = requestAnimationFrame(tick); };
    rafRef.current = requestAnimationFrame(tick);
  };
  const stopAutoScroll = () => {
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
  };
  useEffect(() => () => stopAutoScroll(), []);

  return (
    <div className="space-y-5" data-testid="property-pipeline-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-amber-600">CRM · Pipeline</div>
          <h1 className="mt-1 text-[30px] font-bold text-slate-900 tracking-tight leading-tight">
            Deal Pipeline
          </h1>
          <p className="mt-1.5 text-slate-600 text-[13.5px] max-w-2xl">
            Every property, every stage. Click any card to open its <strong>Command Center</strong> — or launch an Intelligence Engine straight from the card.
          </p>
        </div>
        <Link
          to="/v3/properties?new=1"
          data-testid="pipeline-new-property"
          className="inline-flex items-center gap-1.5 rounded-lg text-white text-[13px] font-semibold px-3.5 py-2 shadow-md hover:shadow-lg transition-all"
          style={{ backgroundImage: "linear-gradient(135deg, #F59E0B, #B45309)" }}
        >
          <Plus className="w-4 h-4" /> New Property
        </Link>
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text" value={q} onChange={(e) => setQ(e.target.value)}
            data-testid="pipeline-search"
            placeholder="Search address, city…"
            className="w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
          />
        </div>

        {/* Deal-type filter chips */}
        <div className="inline-flex items-center gap-1 bg-white border border-slate-200 rounded-lg p-1">
          {[
            { key: "all",              label: "All",              icon: null },
            { key: "house",            label: "House",            icon: Building2 },
            { key: "land",             label: "Land",             icon: TreesIcon },
            { key: "multifamily",      label: "Multifamily",      icon: Building2 },
            { key: "new_construction", label: "New Construction", icon: Building2 },
          ].map(({ key, label, icon: Icon }) => {
            const active = dealTypeFilter === key;
            const count = dealCounts[key] ?? 0;
            return (
              <button
                key={key} type="button"
                onClick={() => setDealTypeFilter(key)}
                data-testid={`pipeline-filter-${key}`}
                className={
                  "inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11.5px] font-semibold transition-colors " +
                  (active
                    ? (key === "land"
                        ? "bg-emerald-500 text-white"
                        : "bg-slate-900 text-white")
                    : "text-slate-600 hover:bg-slate-100")
                }
              >
                {Icon && <Icon className="w-3 h-3" />}
                {label}
                <span className={"text-[10px] tabular-nums " + (active ? "opacity-90" : "text-slate-400")}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Deal-strategy filter dropdown — filters to properties pinned
            to a specific exit strategy (e.g. see just Sub2 deals, or just
            deals with no strategy pinned yet). */}
        {strategyRegistry.length > 0 && (
          <div className="relative">
            <select
              value={strategyFilter}
              onChange={(e) => setStrategyFilter(e.target.value)}
              data-testid="pipeline-strategy-filter"
              className="appearance-none rounded-lg border border-slate-200 bg-white pl-3 pr-8 py-2 text-[11.5px] font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400 cursor-pointer"
            >
              <option value="all">Strategy · All ({strategyCounts.all || 0})</option>
              <option value="__none__">— Un-pinned ({strategyCounts.__none__ || 0})</option>
              {strategyRegistry.map((s) => {
                const cnt = strategyCounts[s.key] || 0;
                if (cnt === 0) return null;
                return (
                  <option key={s.key} value={s.key}>
                    {s.label} ({cnt})
                  </option>
                );
              })}
            </select>
            <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 text-[10px]">▼</span>
          </div>
        )}

        <div className="ml-auto text-[11px] text-slate-500 hidden sm:block">
          Drag cards between columns to update stage · Scroll horizontally to see all stages →
        </div>
      </div>

      {/* Board */}
      {loading ? (
        <div className="flex items-center gap-2 text-slate-600 text-sm" data-testid="pipeline-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading properties…
        </div>
      ) : filtered.length === 0 ? (
        <EmptyBoard hasQuery={!!q.trim()} />
      ) : (
        <div
          ref={boardRef}
          onDragOver={onBoardDragOver}
          onDragEnd={stopAutoScroll}
          data-testid="pipeline-board"
          className="flex gap-4 overflow-x-auto pb-4 -mx-2 px-2"
          style={{ scrollBehavior: "smooth" }}
        >
          {STAGES.map((stage) => {
            const cards = byStage(stage.key);
            const totalArv = cards.reduce(
              (acc, p) => acc + (Number(p.estimated_arv) || 0), 0);
            return (
              <div
                key={stage.key}
                onDragOver={onColDragOver(stage.key)}
                onDragLeave={() => setOverStage((s) => (s === stage.key ? null : s))}
                onDrop={onDrop(stage.key)}
                data-testid={`pipeline-col-${stage.key}`}
                className={
                  "shrink-0 w-[300px] rounded-2xl border bg-white/70 backdrop-blur-sm shadow-sm transition-all " +
                  (overStage === stage.key
                    ? "border-amber-400 ring-2 ring-amber-200"
                    : "border-slate-200")
                }
              >
                {/* Column header */}
                <div
                  className="rounded-t-2xl px-4 py-3 border-b"
                  style={{
                    backgroundColor: `${stage.color}10`,
                    borderColor: `${stage.color}25`,
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="inline-flex items-center gap-2 text-[11px] font-black uppercase tracking-widest"
                         style={{ color: stage.color }}>
                      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: stage.color }} />
                      {stage.label}
                    </div>
                    <div
                      className="text-[11px] font-bold rounded-full px-2 py-0.5 ring-1 tabular-nums"
                      style={{
                        color: stage.color,
                        backgroundColor: "white",
                        borderColor: `${stage.color}30`,
                      }}
                      data-testid={`pipeline-col-count-${stage.key}`}
                    >
                      {cards.length}
                    </div>
                  </div>
                  <div className="mt-1.5 text-[13.5px] font-bold text-slate-800 tabular-nums">
                    {fmtMoney(totalArv)} <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500 ml-1">ARV</span>
                  </div>
                </div>

                {/* Column body */}
                <div className="p-2.5 space-y-2.5 min-h-[160px]">
                  {cards.length === 0 ? (
                    <div
                      className="text-[12px] text-slate-400 italic text-center py-6 border border-dashed border-slate-200 rounded-lg"
                      data-testid={`pipeline-col-empty-${stage.key}`}
                    >
                      Drop a property here
                    </div>
                  ) : (
                    cards.map((p) => (
                      <PipelineCard
                        key={p.id}
                        property={p}
                        stage={stage}
                        isDragging={dragId === p.id}
                        onDragStart={onDragStart(p.id)}
                        onDragEnd={onDragEnd}
                        onOpen={() => nav(`/v3/properties/${p.id}`)}
                        matchCount={matchCounts[p.id]}
                      />
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Pipeline card — draggable, click-to-open-Command-Center, and
// exposes every Intelligence Engine one tap away.
// ─────────────────────────────────────────────────────────────
function PipelineCard({ property: p, stage, isDragging, onDragStart, onDragEnd, onOpen, matchCount }) {
  const nav = useNavigate();
  const scoreInfo = resolveScore(p);
  const kpis = resolveKpis(p);
  const [intelOpen, setIntelOpen] = useState(false);
  const intelRef = useRef(null);
  const showBlastChip = HOT_STAGES_FOR_BLAST.has(p.stage) && (matchCount ?? 0) > 0;

  useEffect(() => {
    if (!intelOpen) return undefined;
    const onDoc = (e) => {
      if (intelRef.current && !intelRef.current.contains(e.target)) setIntelOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [intelOpen]);

  const stop = (e) => { e.preventDefault(); e.stopPropagation(); };

  const openEngine = (engine) => (e) => {
    stop(e);
    setIntelOpen(false);
    nav(`${engine.to}?property_id=${p.id}`);
  };

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onOpen}
      data-testid={`pipeline-card-${p.id}`}
      className={
        "group relative rounded-xl bg-white border border-slate-200 shadow-sm hover:shadow-lg " +
        "hover:-translate-y-0.5 hover:border-slate-300 transition-all cursor-pointer overflow-hidden " +
        (isDragging ? "opacity-40 rotate-1" : "")
      }
      style={{ borderTop: `3px solid ${stage.color}` }}
    >
      {/* Top row: address + score */}
      <div className="px-3.5 pt-3 pb-2 flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-[9.5px] uppercase tracking-widest font-bold text-slate-400 flex items-center gap-1.5 flex-wrap">
            {p.address?.state || "—"}
            {p.deal_type === "land" && (
              <span
                data-testid={`pipeline-card-${p.id}-land-badge`}
                className="inline-flex items-center gap-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 px-1.5 py-[1px] text-[8.5px] font-black tracking-widest"
                title="Land deal"
              >
                <TreesIcon className="w-2.5 h-2.5" /> LAND
              </span>
            )}
            {p.deal_type === "new_construction" && (
              <span
                data-testid={`pipeline-card-${p.id}-newcon-badge`}
                className="inline-flex items-center gap-0.5 rounded-full bg-sky-100 text-sky-800 border border-sky-200 px-1.5 py-[1px] text-[8.5px] font-black tracking-widest"
                title="New construction deal"
              >
                <Building2 className="w-2.5 h-2.5" /> NEW CON
              </span>
            )}
            {p.strategy_key && (
              <span
                data-testid={`pipeline-card-${p.id}-strategy-badge`}
                className="inline-flex items-center gap-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200 px-1.5 py-[1px] text-[8.5px] font-black tracking-widest"
                title={`Deal strategy: ${p.strategy_key}`}
              >
                {(p.strategy_key || "").replace(/_/g, " ").toUpperCase()}
              </span>
            )}
          </div>
          <div className="text-[13.5px] font-bold text-slate-900 leading-tight truncate">
            {p.address?.street || "Unaddressed property"}
            {p.address?.unit ? ` #${p.address.unit}` : ""}
          </div>
          <div className="text-[11px] text-slate-500 flex items-center gap-1 truncate mt-0.5">
            <MapPin className="w-3 h-3 shrink-0" />
            {[p.address?.city, p.address?.zip_code].filter(Boolean).join(", ") || "—"}
          </div>
        </div>
        <DailyBusinessScore
          score={scoreInfo.value}
          size="sm"
          label={scoreInfo.real ? "Score" : "Est"}
          testid={`pipeline-card-${p.id}-score`}
        />
      </div>

      {/* KPI strip */}
      <div className="px-3.5 py-2 grid grid-cols-3 gap-1.5 border-t border-slate-100 bg-slate-50/60">
        <MiniKpi label="ARV"    value={fmtMoney(kpis.arv.value)}    isReal={kpis.arv.real}    color="#3B82F6" />
        <MiniKpi label="MAO"    value={fmtMoney(kpis.mao.value)}    isReal={kpis.mao.real}    color="#F59E0B" />
        <MiniKpi label="Margin" value={fmtMoney(kpis.margin.value)} isReal={kpis.margin.real}
                 color={kpis.margin.value >= 0 ? "#10B981" : "#DC2626"}
                 negative={kpis.margin.value < 0} />
      </div>

      {/* 🎯 Match & Blast chip — only shown when this deal is in a
          disposition-heavy stage AND cached matches exist. Tap → jumps
          straight into the property's Match & Blast tab. */}
      {showBlastChip && (
        <button
          type="button"
          onMouseDown={stop}
          onClick={(e) => { stop(e); nav(`/v3/properties/${p.id}?tab=buyers`); }}
          data-testid={`pipeline-card-${p.id}-blast-chip`}
          className="w-full px-3.5 py-2 flex items-center justify-between gap-2 border-t border-amber-200 bg-gradient-to-r from-amber-100 to-amber-50 hover:from-amber-200 hover:to-amber-100 group/blast transition-colors"
          title={`Blast this deal to ${matchCount} matched cash buyer${matchCount === 1 ? "" : "s"}`}
        >
          <div className="flex items-center gap-1.5 min-w-0">
            <Zap className="w-3.5 h-3.5 text-amber-700 shrink-0" />
            <span className="text-[11.5px] font-black uppercase tracking-[0.06em] text-amber-900 truncate">
              {matchCount} matching {matchCount === 1 ? "buyer" : "buyers"}
            </span>
          </div>
          <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-[0.1em] text-white bg-amber-700 group-hover/blast:bg-amber-800 rounded px-1.5 py-0.5 shrink-0">
            Blast <ArrowRight className="w-2.5 h-2.5" />
          </span>
        </button>
      )}

      {/* Actions */}
      <div className="px-3.5 py-2.5 flex items-center gap-1.5 relative border-t border-slate-100">
        <button
          type="button"
          onMouseDown={stop}
          onClick={onOpen}
          data-testid={`pipeline-card-${p.id}-open`}
          className="flex-1 inline-flex items-center justify-center gap-1 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-[11px] font-semibold px-2 py-1.5 transition-colors"
        >
          <Sparkles className="w-3 h-3" /> Command Center
          <ArrowRight className="w-3 h-3" />
        </button>
        <div ref={intelRef} className="relative">
          <button
            type="button"
            onMouseDown={stop}
            onClick={(e) => { stop(e); setIntelOpen((v) => !v); }}
            data-testid={`pipeline-card-${p.id}-intel`}
            title="Launch an Intelligence Engine on this property"
            className={
              "inline-flex items-center justify-center gap-1 rounded-lg border text-[11px] font-semibold px-2 py-1.5 transition-colors " +
              (intelOpen
                ? "bg-amber-50 border-amber-300 text-amber-800"
                : "border-slate-200 bg-white hover:bg-slate-50 text-slate-700")
            }
          >
            <Brain className="w-3 h-3" />
            Intel
          </button>
          {intelOpen && (
            <div
              onMouseDown={stop}
              onClick={stop}
              data-testid={`pipeline-card-${p.id}-intel-menu`}
              className="absolute right-0 bottom-full mb-1 w-52 rounded-xl border border-slate-200 bg-white shadow-2xl py-1 z-30 overflow-hidden"
            >
              <div className="px-3 pt-2 pb-1 text-[9.5px] uppercase tracking-widest font-bold text-amber-600">
                Intelligence Engines
              </div>
              {INTEL_ENGINES.map((engine) => (
                <button
                  key={engine.key}
                  type="button"
                  onClick={openEngine(engine)}
                  data-testid={`pipeline-card-${p.id}-intel-${engine.key}`}
                  className="w-full text-left flex items-center gap-2 px-3 py-1.5 text-[12px] text-slate-700 hover:bg-amber-50 hover:text-amber-800 transition-colors"
                >
                  <engine.icon className="w-3.5 h-3.5 text-amber-600" />
                  <span className="flex-1">{engine.label}</span>
                  <ArrowRight className="w-3 h-3 opacity-40" />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Reusable sub-components
// ─────────────────────────────────────────────────────────────
function MiniKpi({ label, value, isReal, color, negative }) {
  return (
    <div title={isReal ? "Live from published engine" : "Estimated — publish an engine to lock this in"}>
      <div className="flex items-center gap-1 text-[8.5px] uppercase tracking-widest font-bold text-slate-500">
        <span className="h-1 w-1 rounded-full" style={{ backgroundColor: color }} />
        {label}
        {isReal && (
          <span className="h-1 w-1 rounded-full bg-emerald-500" title="Real value" />
        )}
      </div>
      <div className={`text-[12.5px] font-bold tabular-nums ${
        isReal
          ? (negative ? "text-rose-600" : "text-slate-900")
          : "text-slate-400 italic"
      }`}>
        {value}
      </div>
    </div>
  );
}

function EmptyBoard({ hasQuery }) {
  return (
    <div
      className="rounded-2xl border border-slate-200 bg-white p-14 text-center shadow-sm"
      data-testid="pipeline-empty"
    >
      <Building2 className="w-8 h-8 mx-auto mb-3 text-amber-500" />
      <div className="text-slate-900 font-bold text-lg">
        {hasQuery ? "No properties match your search" : "Your pipeline is empty"}
      </div>
      <div className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
        {hasQuery
          ? "Try a different search or clear the filter to see all deals."
          : "Add your first property to start tracking deals from Lead → Closed."}
      </div>
      {!hasQuery && (
        <Link
          to="/v3/properties"
          data-testid="pipeline-empty-cta"
          className="mt-5 inline-flex items-center gap-1.5 rounded-lg text-white text-sm font-semibold px-4 py-2 shadow-md"
          style={{ backgroundImage: "linear-gradient(135deg, #F59E0B, #B45309)" }}
        >
          <Plus className="w-4 h-4" /> Create Property
        </Link>
      )}
    </div>
  );
}
